<template>
  <button
    ref="chatFab"
    class="chat-fab"
    :class="{
      theme: hasTheme,
      open,
      'has-new-comments': hasNewComments,
    }"
    data-external="signing-page-chat-toggle-button"
    :aria-label="t('open-chat')"
    :style="chatFabStyle"
    @click="$emit('click')"
  >
    <transition name="spin">
      <FriedIcon
        v-if="!open"
        id="open-chat-icon"
        key="1"
        class="open-chat-icon"
        icon="conversation"
      />
      <FriedIcon v-else id="close-chat-icon" key="2" class="close-icon" icon="close" />
    </transition>
    <transition name="bounce">
      <span v-if="newCommentsCount" class="new-comments">{{ newCommentsCount }}</span>
    </transition>
  </button>
</template>

<script lang="ts">
import type { PropType, Ref } from 'vue';
import { computed, defineComponent, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import type { SigningTheme } from '../../signing-theme/types/theme';
import { BrandingHelper } from '../../signing-theme/helpers/branding.helper';

export default defineComponent({
  props: {
    open: {
      type: Boolean,
      required: true,
    },
    newCommentsCount: {
      type: Number,
      required: true,
    },
    theme: {
      type: Object as PropType<SigningTheme>,
      default: null,
    },
    isDsr: {
      type: Boolean,
    },
  },
  emits: ['click'],
  setup(props) {
    const { t } = useI18n();
    const observer: Ref<IntersectionObserver> = ref(null);
    const chatFab: Ref<HTMLButtonElement> = ref(null);

    const hasNewComments = computed(() => props.newCommentsCount > 0);
    const hasTheme = computed(() => !!props.theme && !!props.theme.accentColor);

    const chatFabStyle = computed(() => {
      if (hasTheme.value) {
        return {
          '--chat-background-color': BrandingHelper.getBackgroundColor(
            props.theme.accentColor,
            false,
            false
          ),
          '--chat-background-color-hover': BrandingHelper.getBackgroundColorHover(
            props.theme.accentColor,
            false,
            false
          ),
          '--icon-color': BrandingHelper.getTextColor(props.theme.accentColor, false, false),
        };
      }

      return {};
    });

    onMounted(async () => {
      if (!props.isDsr) {
        return;
      }

      await nextTick();
      const navigationButtons = document.querySelector('div.navigation-buttons') as HTMLDivElement;
      if (!navigationButtons) {
        return;
      }

      observer.value = new IntersectionObserver(
        entries => {
          const [{ isIntersecting }] = entries;
          if (isIntersecting) {
            chatFab.value.classList.add('add-margin');
          }

          if (!isIntersecting) {
            chatFab.value.classList.remove('add-margin');
          }
        },
        {
          threshold: 0.1,
        }
      );

      observer.value.observe(navigationButtons);
    });

    onBeforeUnmount(() => {
      if (!props.isDsr) {
        return;
      }

      if (observer.value) {
        observer.value.disconnect();
      }
    });

    return {
      chatFab,
      chatFabStyle,
      hasTheme,
      hasNewComments,
      t,
    };
  },
});
</script>

<style lang="scss">
@import '../assets/variables';
</style>

<style lang="scss" scoped>
.add-margin {
  margin-bottom: 56px;
}

.chat-fab {
  position: fixed;
  background-color: var(--brand-orange);
  border: none;
  bottom: calc(#{var(--bottombar-height)} + #{var(--spacing-200)});
  right: var(--spacing-150);
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  width: var(--chat-size);
  height: var(--chat-size);
  border-radius: 50%;
  cursor: pointer;
  transition:
    box-shadow,
    margin-bottom 0.2s ease-in-out;
  box-shadow: var(--shadow-2);

  @media only screen and (max-width: $md) {
    right: var(--spacing-100);
  }

  &.has-new-comments {
    animation: shake 5s linear;

    &:not(.open) {
      animation-iteration-count: infinite;
    }
  }

  &:active {
    border: none;
  }

  &:hover,
  &:focus {
    box-shadow: var(--shadow-2);

    &:not(.theme) {
      filter: brightness(85%);
    }
  }

  &:focus:not([data-focus-visible-added]) {
    outline: none;
  }

  .open-chat-icon,
  .close-icon {
    color: var(--white);
    font-weight: bold;
    position: absolute;

    // Fixes position of icon on IE
    top: 30%;
    left: 30%;
  }

  .spin-enter-active,
  .spin-leave-active {
    transition: all 0.3s ease-in-out;
  }

  .spin-enter-from.close-icon,
  .spin-leave-to.close-icon {
    transform: rotate(90deg);
    opacity: 0;
  }

  .spin-enter-from.open-chat-icon,
  .spin-leave-to.open-chat-icon {
    transform: rotate(-90deg);
    opacity: 0;
  }

  .new-comments {
    position: absolute;
    top: -4px;
    right: -4px;
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 50%;
    background-color: var(--error-dark);
    color: var(--white);
    display: flex;
    justify-content: center;
    align-items: center;
    font-weight: normal;
  }

  &.theme {
    background-color: var(--chat-background-color);

    &:hover,
    &:focus {
      background-color: var(--chat-background-color-hover);
    }

    .open-chat-icon,
    .close-icon {
      color: var(--icon-color);
    }
  }
}
</style>
